/**
 * Stripe minimum charge amounts by currency (in minor units - cents/öre/etc.)
 *
 * Reference: https://docs.stripe.com/currencies#minimum-and-maximum-charge-amounts
 *
 * These minimums ensure the Stripe fee doesn't exceed the charge amount.
 * All amounts are in the currency's minor unit (e.g., öre for SEK/NOK).
 *
 * Last updated: 2026-02-06
 */

import { Currency } from '../types/GrooverTypes'

/**
 * Stripe minimum amounts for supported currencies
 */
export const STRIPE_MINIMUM_AMOUNTS: Record<Currency, number> = {
  SEK: 300, // 3.00 SEK (300 öre)
  NOK: 300, // 3.00 NOK
  EUR: 50 // 0.50 EUR (Stripe minimum for EUR)
}

/**
 * Get the minimum charge amount for a given currency
 * @param currency - The currency code (e.g., 'SEK', 'NOK')
 * @returns The minimum amount in minor units (öre/etc.)
 */
export const getStripeMinimumAmount = (currency: Currency): number => {
  return STRIPE_MINIMUM_AMOUNTS[currency]
}

/**
 * Check if an amount meets the Stripe minimum for a given currency
 * @param amount - The amount in minor units
 * @param currency - The currency code
 * @returns true if amount meets or exceeds the minimum
 */
export const meetsStripeMinimum = (amount: number, currency: Currency): boolean => {
  return amount >= getStripeMinimumAmount(currency)
}
